/* eslint-disable react/jsx-props-no-spreading */
import * as React from "react";
import { type Theme, SxProps } from "@mui/material/styles";
import {
  Collapse,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { NavLink } from "react-router-dom";
import { useSidebar } from "../../context/sidebar-provider";

import SidebarItemIcon from "./sidebar-item-icon";
import SidebarNestedMenu from "./sidebar-nested-menu";
import { SidebarItem } from "./sidebar-elements";

export interface SidebarPageItemProps {
  item: SidebarItem;
  action?: React.ReactNode;
  defaultExpanded?: boolean;
  expanded?: boolean;
  selected?: boolean;
  disabled?: boolean;
  nestedNavigation?: React.ReactNode;
  mini: boolean;
  fullyExpanded: boolean;
  fullyCollapsed: boolean;
  onToggle: (id: string, hasNested: boolean) => void;
}

const SidebarPageItem = ({
  item,
  action,
  defaultExpanded = false,
  expanded = defaultExpanded,
  selected = false,
  disabled = false,
  nestedNavigation,
  mini,
  fullyExpanded,
  fullyCollapsed,
  onToggle,
}: SidebarPageItemProps) => {
  const { onPageItemClick } = useSidebar();
  const [isHovered, setIsHovered] = React.useState(false);

  const handleClick = React.useCallback(() => {
    onToggle(item.id, !!nestedNavigation);
    onPageItemClick?.(item.id, !!nestedNavigation);
  }, [onToggle, onPageItemClick, item.id, nestedNavigation]);

  const hasExternalHref =
    item.href?.startsWith("http://") || item.href?.startsWith("https://");

  let nestedNavigationCollapseSx: SxProps<Theme>;
  if (mini && fullyCollapsed) {
    nestedNavigationCollapseSx = {
      fontSize: 18,
      position: "absolute",
      top: "41.5%",
      right: 2,
      transform: "translateY(-50%) rotate(-90deg)",
    };
  } else if (!mini && fullyExpanded) {
    nestedNavigationCollapseSx = {
      ml: 0.5,
      fontSize: 20,
      transform: `rotate(${expanded ? 0 : -90}deg)`,
      transition: (theme: Theme) =>
        theme.transitions.create("transform", {
          easing: theme.transitions.easing.sharp,
          duration: 100,
        }),
    };
  } else {
    nestedNavigationCollapseSx = { display: "none" };
  }

  const buttonProps = {
    selected,
    disabled,
    sx: { height: mini ? 50 : "auto" },
    ...(nestedNavigation && !mini && { onClick: handleClick }),
    ...(!nestedNavigation && {
      component: NavLink,
      to: item.href,
      onClick: handleClick,
      ...(hasExternalHref && {
        target: "_blank",
        rel: "noopener noreferrer",
      }),
    }),
  };

  return (
    <>
      <ListItem
        key={`sidebar-page-item-${item.id}`}
        data-testid={`sidebar-page-item-${item.id}`}
        disablePadding
        onMouseEnter={
          nestedNavigation && mini ? () => setIsHovered(true) : undefined
        }
        onMouseLeave={
          nestedNavigation && mini ? () => setIsHovered(false) : undefined
        }
        sx={{
          display: "block",
          py: 0,
          px: item.nested && mini ? 0 : 1,
          overflowX: "hidden",
        }}
      >
        <ListItemButton {...buttonProps}>
          <SidebarItemIcon
            mini={mini}
            nested={item.nested || false}
            title={item.title}
            icon={item.icon}
            selected={selected}
          />

          {!mini && (
            <>
              <ListItemText
                primary={item.title}
                sx={{ whiteSpace: "nowrap", zIndex: 1 }}
              />
              {action && fullyExpanded && action}
            </>
          )}

          {nestedNavigation && (
            <ExpandMoreIcon sx={nestedNavigationCollapseSx} />
          )}
        </ListItemButton>

        {nestedNavigation && mini && (
          <SidebarNestedMenu open={isHovered}>
            {nestedNavigation}
          </SidebarNestedMenu>
        )}
      </ListItem>

      {nestedNavigation && !mini && (
        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          key={`sidebar-nested-page-item-${item.id}`}
        >
          {nestedNavigation}
        </Collapse>
      )}
    </>
  );
};

export default SidebarPageItem;
